// src/pages/CartPage.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatPrice, detectCountry } from '../services/shopApi';
import './CartPage.css';

export default function CartPage({ cart, totals, onUpdate, onRemove }) {
  const navigate = useNavigate();
  const [promoCode,      setPromoCode]      = useState('');
  const [promoMsg,       setPromoMsg]       = useState('');
  const [detectedCurrency, setDetectedCurrency] = useState('USD'); // ← NEW

  const { subtotal = 0, shippingCost = 0, shipping = 0, tax = 0, total = 0 } = totals || {};
  const shipCost = shippingCost || shipping;

  // ── FIX: Auto-detect currency from visitor country ──────────────
  // Previously defaulted to 'USD' — Vietnamese users saw $ instead of ₫
  // Now: totals.currency (backend) → item.currency → detected country currency
  useEffect(() => {
    if (totals?.currency) {
      setDetectedCurrency(totals.currency);
      return;
    }
    if (cart.items?.[0]?.currency) {
      setDetectedCurrency(cart.items[0].currency);
      return;
    }
    // Fall back to geo-detected currency (VN → VND, US → USD, etc.)
    detectCountry().then(geo => {
      if (geo?.currency) setDetectedCurrency(geo.currency);
    });
  }, [totals, cart.items]);
  // ────────────────────────────────────────────────────────────────

  const cartCurrency = totals?.currency || cart.items?.[0]?.currency || detectedCurrency;
  const fmtCart = (amount, cur) => formatPrice(amount, cur || cartCurrency);

  // Currency breakdown for mixed-currency carts
  const breakdown = totals?.currencyBreakdown || [];

  const amountToFreeShip = Math.max(0, 50 - subtotal);

  const handlePromo = () => {
    if (!promoCode.trim()) return;
    setPromoMsg('Promo code feature coming soon.');
  };

  if (!cart.items?.length) {
    return (
      <div className="page-wrap">
        <div className="empty-state" style={{ marginTop: 60 }}>
          <div className="icon">🛒</div>
          <h3>Your cart is empty</h3>
          <p>Looks like you haven't added anything yet.</p>
          <button
            className="btn-primary"
            style={{ marginTop: 24 }}
            onClick={() => navigate('/products')}
          >
            Start Shopping →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page page-wrap">
      <div className="cart-title">
        Shopping Cart{' '}
        <span style={{ color:'var(--text-muted)', fontWeight:500, fontSize:18 }}>
          ({cart.totalItems} {cart.totalItems === 1 ? 'item' : 'items'})
        </span>
      </div>

      <div className="cart-layout">

        {/* ── Items panel ─────────────────────────────────────────────── */}
        <div className="cart-panel">
          <div className="cart-panel-head">
            <h2>Items</h2>
            <span style={{ fontSize:12, color:'var(--text-dim)' }}>
              {cart.items.length} {cart.items.length === 1 ? 'product' : 'products'}
            </span>
          </div>

          {cart.items.map(item => {
            // Each item may have its own currency (multi-market cart)
            const itemCurrency = item.currency || cartCurrency;
            const lineTotal    = item.price * item.quantity;

            return (
              <div key={item._id?.toString() || item.productStringId || Math.random()} className="cart-row">
                {/* Image */}
                <div className="cart-img">
                  {item.image
                    ? <img
                        src={item.image}
                        alt={item.name}
                        onError={e => { e.currentTarget.style.display='none'; }}
                      />
                    : '📦'}
                </div>

                {/* Info */}
                <div className="cart-info">
                  <div className="cart-name">{item.name}</div>
                  {item.variant?.label && (
                    <div className="cart-variant">{item.variant.label}</div>
                  )}
                  {item.businessName && (
                    <div className="cart-seller">{item.businessName}</div>
                  )}
                  {item.freeShipping
                    ? <div className="cart-free">✓ Free Delivery</div>
                    : null}

                  <div className="cart-actions">
                    <div className="qty-box">
                      <button
                        className="qty-btn"
                        onClick={() => onUpdate(item._id, item.quantity - 1)}
                        aria-label="Decrease quantity"
                        disabled={item.quantity <= 1}
                      >−</button>
                      <span className="qty-num">{item.quantity}</span>
                      <button
                        className="qty-btn"
                        onClick={() => onUpdate(item._id, item.quantity + 1)}
                        aria-label="Increase quantity"
                        disabled={item.quantity >= (item.maxQuantity || 99)}
                      >+</button>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => onRemove(item._id?.toString() || item.productStringId)}
                      aria-label={`Remove ${item.name}`}
                    >🗑 Remove</button>
                  </div>
                </div>

                {/* Price — always use item's own currency */}
                <div className="cart-row-right">
                  <div className="cart-price">
                    {formatPrice(lineTotal, itemCurrency)}
                  </div>
                  {item.quantity > 1 && (
                    <div className="cart-unit">
                      {formatPrice(item.price, itemCurrency)} each
                    </div>
                  )}
                  {item.originalPrice > item.price && (
                    <div className="cart-saving">
                      Save {formatPrice(
                        (item.originalPrice - item.price) * item.quantity,
                        itemCurrency
                      )}
                    </div>
                  )}
                  <div className="cart-item-currency">{itemCurrency}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Summary sidebar ──────────────────────────────────────────── */}
        <div className="cart-summary-sticky">
          <div className="cart-summary">
            <div className="summary-head">Order Summary</div>
            <div className="summary-body">

              <div className="summary-row">
                <span>Subtotal ({cart.totalItems} items)</span>
                <span>{fmtCart(subtotal)}</span>
              </div>

              <div className="summary-row">
                <span>Shipping</span>
                <span className={shipCost === 0 ? 'free' : ''}>
                  {shipCost === 0 ? '✓ Free' : fmtCart(shipCost)}
                </span>
              </div>

              {shipCost > 0 && amountToFreeShip > 0 && (
                <div className="threshold-note">
                  Add {fmtCart(amountToFreeShip)} more for free shipping!
                </div>
              )}

              {tax > 0 && (
                <div className="summary-row">
                  <span>Tax (10%)</span>
                  <span>{fmtCart(tax)}</span>
                </div>
              )}

              <div className="summary-row total">
                <span>Total</span>
                <span>{fmtCart(total)}</span>
              </div>

              {/* Multi-currency breakdown */}
              {breakdown.length > 0 && (
                <div className="cart-currency-breakdown">
                  <div className="breakdown-label">Currency breakdown</div>
                  {breakdown.map(b => (
                    <div key={b.currency} className="breakdown-row">
                      <span>{b.currency} subtotal ({b.itemCount} items)</span>
                      <span>{formatPrice(b.subtotal, b.currency)}</span>
                    </div>
                  ))}
                </div>
              )}

              <button
                className="btn-primary checkout-cta"
                onClick={() => navigate('/checkout')}
              >
                Checkout →
              </button>
              <Link to="/products" className="continue-link">← Continue Shopping</Link>
            </div>

            {/* Promo code */}
            <div className="promo-area">
              <div className="promo-label">Promo Code</div>
              <div className="promo-row">
                <input
                  className="promo-input"
                  placeholder="Enter code…"
                  value={promoCode}
                  onChange={e => { setPromoCode(e.target.value); setPromoMsg(''); }}
                  onKeyDown={e => e.key === 'Enter' && handlePromo()}
                />
                <button className="promo-btn" onClick={handlePromo}>Apply</button>
              </div>
              {promoMsg && (
                <div className="promo-msg">{promoMsg}</div>
              )}
            </div>
          </div>

          {/* Trust badges */}
          <div className="cart-trust">
            {[
              { ico:'🛡️', t:'Secure checkout' },
              { ico:'✅', t:'All products authenticity verified' },
              { ico:'↩️', t:'30-day easy returns' },
            ].map(r => (
              <div key={r.t} className="trust-row">
                <span className="ico">{r.ico}</span>{r.t}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}